import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import {
  ChallengeResponse,
  ReauthConfig,
  REAUTHENTICATE,
  ReauthenticateService,
} from '@backbase/foundation-ang/web-sdk';
import { from, Observable, Subject, Subscriber, throwError } from 'rxjs';
import { catchError, first, mapTo } from 'rxjs/operators';
import { hasStatusWithHeaderValue } from '../common/helpers';
// import { TransactionSigningConfig, TRANSACTION_SIGNING_CONFIG } from './transaction-signing.config';
import {
  CapabilityChallenge,
  CapabilityData,
  CapabilityResponse,
  TransactionSigningHandler,
} from './transaction-signing.model';

/**
 * Angular Service used to instigate and manage transaction signing for an action.
 */
@Injectable({
  providedIn: 'root',
})
export class TransactionSigningService {
  readonly requestSubject = new Subject<TransactionSigningHandler>();
  private isHandlingTransaction = false;
  private readonly authHeaderKey = 'www-authenticate';
  private readonly authHeaderValue = 'Bearer challenge_types=reauth';
  private readonly authStatus = 401;

  constructor(
    @Optional()
    @Inject(REAUTHENTICATE)
    private readonly reauthService: ReauthenticateService,
  ) {}

  /**
   * @internal
   * Initiates the transaction signing workflow.
   * @param error the error response from a capability that requests authentication
   * @returns an Observable used to handle responses on completion or error
   */
  initiateTransactionSigning(error: HttpErrorResponse): Observable<HttpResponse<any>> {
    if (
      !hasStatusWithHeaderValue(error, this.authStatus, this.authHeaderKey, this.authHeaderValue) ||
      this.isHandlingTransaction
    ) {
      return throwError(error);
    }

    if (!this.reauthService) {
      console.error('WebSDK version 1.13.0 or higher is required to perform reauthentication.');
      return throwError(error);
    }

    const body: CapabilityResponse = error.error || {};
    const challenge = this.getFirstChallenge(body);
    return challenge ? this.authenticateUser(challenge, body.data) : throwError(error);
  }

  private getFirstChallenge(body: CapabilityResponse): CapabilityChallenge | undefined {
    const challenges: Array<CapabilityChallenge> = body.challenges || [];
    return challenges.find((c) => c.scope !== undefined);
  }

  private authenticateUser(
    challenge: CapabilityChallenge,
    transactionData: CapabilityData,
  ): Observable<HttpResponse<any>> {
    return this.reauthenticate(challenge).pipe(
      first(),
      mapTo(new HttpResponse({ body: transactionData })),
      catchError((response: ChallengeResponse) => {
        return new Observable<HttpResponse<any>>((observer) => {
          this.isHandlingTransaction = true;
          this.requestSubject.next({
            success: this.successFunction(observer),
            failure: this.failureFunction(observer),
            challenge: response,
            transactionData,
          });
        });
      }),
    );
  }

  private successFunction = (observer: Subscriber<HttpResponse<any>>) => {
    return (data: any) => {
      observer.next(new HttpResponse({ body: data }));
      observer.complete();
      this.isHandlingTransaction = false;
    };
  };

  private failureFunction = (observer: Subscriber<HttpResponse<any>>) => {
    return (error: Error, data?: any) => {
      observer.error(
        new HttpErrorResponse({
          status: 401,
          statusText: error.message,
          error: {
            error,
            data,
          },
        }),
      );
      this.isHandlingTransaction = false;
    };
  };

  private reauthenticate(challenge: CapabilityChallenge) {
    const { acrValues, scope } = challenge;
    return from(
      this.reauthService.reauthenticate(scope, acrValues, {
        redirect: false,
      }),
    );
  }

  private getReauthConfig(): ReauthConfig | undefined {
    if (false) {
      return {
        redirectUri: location.href,
        redirect: true,
      };
    }
    return undefined;
  }
}
